/**
 * Accounts, classes, homework and teacher exercises (docs/accounts-classes-plan.md). Spread into
 * `en`; `ro-accounts.ts` is typed against this object like the main catalog (ADR 0008).
 */
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const enAccounts = {
  auth: {
    email: 'Email',
    password: 'Password',
    show: 'Show',
    hide: 'Hide',
    loginTitle: 'Log in',
    loginSubmit: 'Log in',
    forgotLink: 'Forgot your password?',
    newHere: 'New here?',
    createAccount: 'Create an account',
    invalid: 'Wrong email or password.',
    disabled: 'This account was disabled. Ask your teacher or write to us.',
    tooMany: 'Too many tries. Wait a minute and try again.',
    signupTitle: 'Create an account',
    signupText: 'Keep your work on every computer, join your class and get homework.',
    displayName: 'Name others see',
    displayNameHint: 'A nickname is fine — no need for your full name.',
    passwordHint: 'At least 8 characters',
    born: 'Month and year you were born',
    month: 'Month',
    year: 'Year',
    parentEmail: "A parent's email",
    parentHint:
      'You are under 16, so a parent needs to agree. We send them one email; until they answer, you cannot join a class.',
    wantsTeacher: 'I am a teacher (an admin checks and approves this)',
    acceptPrivacy: 'I have read what PebbleCode keeps about me:',
    privacyLink: 'privacy notice',
    signupSubmit: 'Create account',
    haveAccount: 'Already have an account?',
    emailTaken: 'There is already an account with this email. Log in instead?',
    parentRequired: "Add a parent's email.",
    parentSame: "The parent's email must be different from yours.",
    forgotTitle: 'Forgot your password?',
    forgotText: 'Type your email. If it has an account, we send a link to choose a new password.',
    forgotSubmit: 'Send the link',
    forgotSent: (email: string) =>
      `If there is an account for ${email}, the link is on its way. Check your inbox (and spam).`,
    backToLogin: 'Back to log in',
    resetTitle: 'Choose a new password',
    newPassword: 'New password',
    resetSubmit: 'Save and log in',
    linkInvalid: 'This link does not work any more — it was used already or it expired.',
    askNewLink: 'Ask for a new link',
    verifying: 'Confirming your email…',
    verified: 'Your email is confirmed. Thank you!',
    continue: 'Go to PebbleCode',
    unverified: (email: string) => `Confirm your email: we sent a link to ${email}.`,
    consentPending: (parent: string) =>
      `A parent needs to agree before you can join a class. We emailed ${parent}.`,
    resend: 'Send it again',
    resent: 'Sent!',
    claimed: (projects: number, points: number) =>
      `Your work from this computer is now in your account: ${plural(projects, 'project', 'projects')}, ${plural(points, 'point', 'points')}.`,
  },
  menu: {
    label: 'Account menu',
    login: 'Log in',
    signup: 'Sign up',
    account: 'My account',
    classes: 'My classes',
    exercises: 'My exercises',
    admin: 'Users',
    logout: 'Log out',
  },
  account: {
    title: 'My account',
    profile: 'Profile',
    save: 'Save',
    saved: 'Saved',
    role: { student: 'Student', teacher: 'Teacher', admin: 'Admin' },
    youAre: 'Account type',
    askTeacher: 'I am a teacher — ask for teacher access',
    teacherPending: 'Your teacher request is waiting for an admin.',
    teacherRejected: 'An admin declined your teacher request. Write to us if this is a mistake.',
    passwordTitle: 'Password',
    currentPassword: 'Current password',
    changePassword: 'Change password',
    passwordChanged: 'Password changed. Other devices were logged out.',
    wrongPassword: 'That password is wrong.',
    parentTitle: "Parent's consent",
    consentGranted: 'A parent agreed. Your account is complete.',
    deleteTitle: 'Delete my account',
    deleteText:
      'Deletes your account, projects, points, classes and class memberships at once. It cannot be undone.',
    deletePassword: 'Type your password to confirm',
    deleteButton: 'Delete my account forever',
  },
  admin: {
    title: 'Users',
    search: 'Search by name or email',
    filters: {
      all: 'All',
      'teacher-requests': 'Teacher requests',
      teachers: 'Teachers',
      admins: 'Admins',
      disabled: 'Disabled',
    },
    count: (n: number) => plural(n, 'user', 'users'),
    name: 'Name',
    role: 'Role',
    status: 'Status',
    joined: 'Joined',
    actions: 'Actions',
    approve: 'Approve teacher',
    reject: 'Decline',
    disable: 'Disable',
    enable: 'Enable',
    delete: 'Delete',
    deleteConfirm: (name: string) => `Delete ${name}'s account and all their work?`,
    sendReset: 'Send password link',
    resetSent: 'Link sent',
    unverified: 'email not confirmed',
    consentPending: 'waiting for a parent',
    requested: 'wants to teach',
    rejected: 'teacher request declined',
    disabled: 'disabled',
    none: 'No users found.',
    more: 'Show more',
    changeRole: (name: string) => `Role of ${name}`,
  },
  parent: {
    consentTitle: (name: string) => `${name} wants a PebbleCode account`,
    consentIntro: (name: string, email: string) =>
      `${name} (${email}) signed up on PebbleCode, where students learn programming with blocks and real code, and gave your email as their parent's.`,
    keepTitle: 'What we keep',
    keep: [
      "The student's email, the name they chose and their month and year of birth.",
      'Your email, to ask you and so you can withdraw later.',
      'Their projects and points, and the classes they join.',
    ],
    notTitle: 'What we never do',
    not: ['No ads, no selling data, no public profile.', 'Other students never see their email.'],
    approve: 'I agree',
    deny: 'I do not agree — delete the account',
    denyConfirm: 'Delete the account and all its data now?',
    approved:
      'Thank you — the account is complete. We emailed you a link to see it or withdraw your consent later.',
    denied: 'The account and all its data were deleted.',
    manageTitle: (name: string) => `${name}'s PebbleCode account`,
    manageText:
      'You agreed to this account. You can withdraw at any time: the account and everything in it is deleted at once.',
    withdraw: 'Withdraw consent and delete the account',
    withdrawConfirm: 'Delete the account and all its data now? This cannot be undone.',
    withdrawn: 'The account and all its data were deleted.',
  },
  privacy: {
    title: 'Privacy notice',
    draft: 'Draft — to be reviewed by a lawyer before launch.',
    sections: [
      [
        'Without an account',
        'Nothing personal is stored. Your browser keeps a random key; your projects and points are saved under that key only.',
      ],
      [
        'With an account',
        'We keep your email, the name you chose, your month and year of birth, your projects, points and classes, and when you last logged in. Passwords are stored only as a one-way hash.',
      ],
      [
        'Students under 16',
        "We also keep a parent's email and ask them to agree. Until they do, the account cannot join classes; without an answer it is deleted after 14 days. The parent can withdraw at any time, which deletes the account.",
      ],
      [
        'Teachers',
        'A teacher sees the names and emails of the students in their classes, their homework and the work they do for it. Students never see each other’s emails.',
      ],
      [
        'AI tutor',
        'Questions to the tutor send the program and the exercise to an AI provider, never your name or email.',
      ],
      [
        'Deleting',
        'You can delete your account from the account page; everything is deleted at once. Emails are sent through Resend.',
      ],
    ] as [string, string][],
  },
  classes: {
    title: 'My classes',
    newClass: 'New class',
    namePlaceholder: 'e.g. 7B, or Tuesday group',
    create: 'Create',
    emptyTeacher: 'No classes yet. Create one and give its code to your students.',
    emptyStudent: 'You are not in a class yet. Ask your teacher for the class code.',
    students: (n: number) => plural(n, 'student', 'students'),
    teacher: (name: string) => `Teacher: ${name}`,
    joinCode: 'Join code',
    joinLink: 'Join link',
    newCode: 'New code',
    newCodeConfirm:
      'Make a new code? The old one stops working; students already in the class stay.',
    rename: 'Rename',
    delete: 'Delete class',
    deleteConfirm: (name: string) =>
      `Delete ${name}? Its homework and the students' hand-ins go too (their projects stay).`,
    roster: 'Students',
    noStudents: 'No students yet. Share the join link or the code.',
    remove: 'Remove',
    removeConfirm: (name: string) => `Remove ${name} from the class?`,
    leave: 'Leave class',
    leaveConfirm: (name: string) => `Leave ${name}?`,
    joinTitle: 'Join a class',
    joinText: 'Type the 8-character code your teacher gave you.',
    joinPlaceholder: 'CLASS CODE',
    join: 'Join',
    joinInvalid: 'A class code has 8 letters and digits.',
    joinNotFound: 'No class has this code.',
    joinNeedsLogin: (name: string) => `Log in or create an account to join ${name}.`,
    joinConfirm: (name: string, teacher: string) => `Join ${name}, ${teacher}'s class?`,
    alreadyMember: (name: string) => `You are already in ${name}.`,
    notActive:
      "Your account needs a confirmed email (and, under 16, a parent's consent) before joining a class.",
    teachersOnly: 'Only teachers can create classes. Ask for teacher access on your account page.',
    open: 'Open',
    back: 'All classes',
  },
  classLive: {
    liveNow: 'Live now',
    liveWith: (teacher: string, className: string) => `${teacher} · ${className}`,
    join: 'Join',
    broadcast: 'Broadcast to a class',
    broadcastNone: 'No class — links only',
    broadcastHint: (name: string) =>
      `Students of ${name} see “Live now” on their home page and can watch (logged in).`,
    start: 'Go live',
    classOnly: 'This session is for a class. Log in with an account in that class to watch.',
    sharedWith: (name: string) => `Broadcast to ${name}`,
  },
  homework: {
    title: 'Homework',
    none: 'No homework right now.',
    due: (date: string) => `Due ${date}`,
    noDue: 'No due date',
    late: 'late',
    status: {
      todo: 'Not started',
      started: 'In progress',
      solved: 'Solved',
      submitted: 'Handed in',
    },
    open: 'Open',
    handIn: 'Hand in',
    takeBack: 'Take back',
    feedback: 'Feedback from your teacher',
    projectNote: 'No automatic check here: press Hand in when you are done.',
    assign: 'Assign homework',
    kind: {
      exercise: 'An exercise from the catalog',
      custom: 'One of my exercises',
      project: 'One of my projects (as the starting program)',
    },
    pick: 'Choose…',
    itemTitle: 'Title',
    note: 'Note for the students (optional)',
    dueDate: 'Due date (optional)',
    create: 'Assign',
    progress: (done: number, total: number) => `${done} of ${total} done`,
    startedCount: (n: number) => `${n} started`,
    seeWork: 'See work',
    delete: 'Delete',
    deleteConfirm: (title: string) => `Delete “${title}” and the students' hand-ins?`,
    noneTeacher: 'No homework yet.',
    student: 'Student',
    lastEdit: 'Last edit',
    handedIn: 'Handed in',
    openWork: 'Open work',
    notOpened: 'Not opened yet',
    feedbackPlaceholder: 'A few words for the student…',
    saveFeedback: 'Save feedback',
    feedbackSaved: 'Feedback saved',
    reviewing: (student: string, title: string) => `${student} · ${title}`,
    viewOnly: 'view only',
    back: 'Back to the homework',
    fromClass: (name: string) => `for ${name}`,
  },
  teach: {
    title: 'My exercises',
    newExercise: 'New exercise',
    empty: 'You have not written any exercises yet.',
    edit: 'Edit',
    delete: 'Delete',
    deleteConfirm: (title: string) => `Delete “${title}”? Homework using it is deleted too.`,
    formTitle: 'Title',
    summary: 'One-line summary (optional)',
    statement: 'Task',
    statementHint: 'Mark code with `backticks` and bold with **stars**.',
    lang: 'Language of the text',
    category: 'Category',
    difficulty: 'Difficulty',
    examples: 'Examples',
    examplesHint:
      'The program is checked on every example. Hidden ones are checked too, but students never see them.',
    input: 'Input (one value per line)',
    output: 'Expected output',
    hidden: 'Hidden',
    addExample: 'Add example',
    removeExample: 'Remove example',
    exampleN: (i: number) => `Example ${i}`,
    starter: 'Starting program (optional)',
    starterNone: 'Empty program',
    starterKeep: 'Keep the current starting program',
    save: 'Save',
    saved: 'Saved',
    needVisible: 'At least one example must be visible.',
    hiddenFailing: (n: number) =>
      `Every example you can see passes, but ${n === 1 ? '1 hidden check fails' : `${n} hidden checks fail`}. Think about other inputs.`,
    byTeacher: 'From your teacher',
    back: 'My exercises',
  },
};
